import { UseFormSetValue } from 'react-hook-form'
import i18next from 'i18next'
import { FormInputProps, SelectOption } from '@microservices/wiskey-react-components'

import {
  ACTION_CODE,
  EVENT_CODE,
  EVENT_CODE_GANTT,
  GENERATOR_INPUT_TYPE,
  MAX_INPUT_LENGTH,
  REGEX,
} from '@constants'
import { AutocompleteOption, FormCommand, GETVariableDTO, ModalType } from '@types'

export type ActionForm = {
  id?: number | string
  formToLcConnectionId?: number
  code: string
  title: string
  actionCode: ACTION_CODE | null | undefined
  actionTitle?: string
  eventCode?: EVENT_CODE | EVENT_CODE_GANTT | null
  eventTitle?: string
  params?: string
  columnToSort?: AutocompleteOption | null
  order?: string | number
  json?: boolean
  jsValue?: string
  variable: GETVariableDTO | null
  objectFormCode?: AutocompleteOption | null
  targetColumn?: AutocompleteOption | null
  formObject: AutocompleteOption | null
  contextMenu?: AutocompleteOption<number> | null
  tooltip?: AutocompleteOption<number> | null
  command?: FormCommand[] | null
  viewPreFilterScript?: string
}

type useInputsParams = {
  setValue: UseFormSetValue<ActionForm>
  actionTypeOptions: SelectOption[]
  contextMenusOptions: AutocompleteOption[]
  tooltipOptions: AutocompleteOption[]
  targetColumnsOptions: AutocompleteOption[]
  eventOptions: SelectOption[]
  variablesOptions: AutocompleteOption[]
  objectOptions: AutocompleteOption[]
  watchJson?: boolean
  watchEvent?: EVENT_CODE | EVENT_CODE_GANTT | null
  watchActionCode?: ACTION_CODE | null
  watchFormObject?: AutocompleteOption | null
  formCodeOptions: AutocompleteOption[]
  loadingAllForm?: boolean
  loadingBindingValuesEvents?: boolean
  isAllObjectsLoading: boolean
  isOpenFormAction: boolean
  modalType: ModalType
  setIsOpenScriptValueDialog: (value: boolean) => void
  setCurrentNameScriptDialog: (value: string) => void
}

export const useInputs = ({
  setValue,
  actionTypeOptions,
  eventOptions,
  objectOptions,
  watchJson,
  watchEvent,
  watchActionCode,
  watchFormObject,
  formCodeOptions,
  loadingAllForm,
  targetColumnsOptions,
  contextMenusOptions,
  variablesOptions,
  tooltipOptions,
  loadingBindingValuesEvents,
  isAllObjectsLoading,
  isOpenFormAction,
  modalType,
  setIsOpenScriptValueDialog,
  setCurrentNameScriptDialog,
}: useInputsParams) => {
  const isRightColumnClick = [
    EVENT_CODE.ON_COLUMN_RIGHT_CLICK,
    EVENT_CODE.ON_HEADER_TOOLTIP,
    EVENT_CODE.ON_CELL_TOOLTIP,
  ].includes(watchEvent as EVENT_CODE)

  const isContextMenu = watchActionCode === ACTION_CODE.OPEN_CONTEXT_MENU
  const isTooltip = watchActionCode === ACTION_CODE.OPEN_TOOLTIP
  const isVariableInput =
    watchEvent === EVENT_CODE.ON_VARIABLE_CHANGE ||
    watchActionCode === ACTION_CODE.CHANGE_VARIABLE_VALUE
  const isEmptyActionCode = !watchActionCode
  const isShowJSValueInput =
    watchActionCode === ACTION_CODE.SET_DATA ||
    watchActionCode === ACTION_CODE.CHANGE_VARIABLE_VALUE ||
    watchActionCode === ACTION_CODE.MODIFY_DATA

  const targetInput = isRightColumnClick
    ? [
        {
          name: 'targetColumn',
          inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
          placeholder: i18next.t('entityActionDialog.targetColumn.placeholder'),
          label: i18next.t('entityActionDialog.targetColumn.label'),
          autocompleteOptions: targetColumnsOptions,
          loading: loadingAllForm,
          rules: { required: true },
        },
      ]
    : []

  const variableInput = [
    {
      name: 'variable',
      disableClearable: true,
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      label: i18next.t('actionInputs.variable.label'),
      autocompleteOptions: variablesOptions,
      rules: { required: true },
    },
  ]

  const contextMenuOrTooltipInput = isContextMenu
    ? [
        ...targetInput,
        {
          name: 'contextMenu',
          inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
          placeholder: i18next.t('entityActionDialog.contextMenu.placeholder'),
          label: i18next.t('entityActionDialog.contextMenu.label'),
          autocompleteOptions: contextMenusOptions,
          loading: loadingAllForm,
          rules: { required: true },
        },
      ]
    : [
        ...targetInput,
        {
          name: 'tooltip',
          inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
          placeholder: i18next.t('entityActionDialog.tooltip.placeholder'),
          label: i18next.t('entityActionDialog.tooltip.label'),
          autocompleteOptions: tooltipOptions,
          loading: loadingAllForm,
          rules: { required: true },
        },
      ]

  const actionValueInputs = [
    {
      name: 'formCodeRow',
      inputs: [
        ...(isOpenFormAction && !watchJson
          ? [
              {
                name: 'formObject',
                inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
                label: i18next.t('actionInputs.formObject.label'),
                placeholder: i18next.t('actionInputs.formObject.placeholder'),
                autocompleteOptions: objectOptions,
                loading: isAllObjectsLoading,
                rules: {
                  required: true,
                  validate: (value: string) =>
                    value !== i18next.t('actionInputs.formObject.placeholder'),
                },
                onChangeAutocomplete: () => {
                  setValue('objectFormCode', null)
                },
              },
              {
                name: 'objectFormCode',
                inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
                placeholder: i18next.t('placeholder.objectFormCode'),
                label: i18next.t('label.objectFormCode'),
                autocompleteOptions: formCodeOptions,
                loading: loadingAllForm,
                rules: { required: true },
                disabled: !watchFormObject,
              },
            ]
          : []),
      ],
    },
    ...(isShowJSValueInput
      ? [
          {
            disableClearable: true,
            inputType: GENERATOR_INPUT_TYPE.INPUT,
            label: i18next.t('actionInputs.jsValue.label'),
            labelSx: { minWidth: 120 },
            name: 'jsValue',
            placeholder: i18next.t('actionInputs.jsValue.placeholder'),
            readOnly: true,
            additionalBtn: {
              isEnabled: true,
              text: 'edit',
              color: 'primary' as const,
              variant: 'contained' as const,
              onClick: () => {
                setIsOpenScriptValueDialog(true)
                setCurrentNameScriptDialog('jsValue')
              },
            },
            formInputContainerSx: {
              flexWrap: 'nowrap',
              '& .MuiFormControlLabel-root': {
                mb: 1,
              },
              '& .MuiButton-root': {
                ml: 0.5,
              },
            },
            rules: {
              maxLength: MAX_INPUT_LENGTH,
            },
          },
        ]
      : []),
    {
      name: 'json',
      inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
      label: i18next.t('label.json'),
      labelPlacement: 'end',
    },
    ...(watchJson
      ? [
          {
            name: 'params',
            inputType: GENERATOR_INPUT_TYPE.TEXTAREA,
            label: i18next.t('actionInputs.params.label'),
            placeholder: i18next.t('actionInputs.params.placeholder'),
            rules: { required: true, validate: value => value.trim().length !== 0 },
          },
        ]
      : []),
  ]

  const actionInputs: FormInputProps[] = [
    {
      name: 'code',
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      placeholder: i18next.t('actionInputs.code.placeholder'),
      replacePattern: REGEX.MODEL_CODE_REPLACE_PATTERN,
      label: i18next.t('actionInputs.code.label'),
      disabled: modalType === 'edit',
      rules: { required: true, validate: value => value.trim().length !== 0 },
    },
    {
      name: 'title',
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      placeholder: i18next.t('actionInputs.title.placeholder'),
      label: i18next.t('actionInputs.title.label'),
      rules: { required: true, validate: value => value.trim().length !== 0 },
    },
    {
      name: 'actionCode',
      inputType: GENERATOR_INPUT_TYPE.SELECT,
      label: i18next.t('actionInputs.actionCode.label'),
      placeholder: i18next.t('placeholder.actionType'),
      selectOptions: actionTypeOptions,
      rules: { required: true },
      onChangeSelect: value => {
        if (value !== watchActionCode) {
          setValue('eventCode', null, { shouldDirty: true })
        }

        setValue('objectFormCode', null)
        setValue('formObject', null)
      },
    },
    {
      name: 'eventCode',
      inputType: GENERATOR_INPUT_TYPE.SELECT,
      label: i18next.t('actionInputs.eventCode.label'),
      placeholder: i18next.t('placeholder.event'),
      selectOptions: isEmptyActionCode ? [] : eventOptions,
      loading: loadingBindingValuesEvents,
      rules: { required: true },
    },
    ...(isVariableInput ? variableInput : []),
    ...(isContextMenu || isTooltip ? contextMenuOrTooltipInput : actionValueInputs),
  ]

  return actionInputs
}
